import React, { useEffect, useRef, useState } from 'react';
import { useCall } from '../../context/CallContext';

/**
 * Active Call Screen - Full screen video/audio call UI
 * Supports calls between iOS and Web users
 */
export default function ActiveCallScreen() {
  const {
    currentCall,
    callStatus,
    localStream,
    remoteStream,
    hangUp,
    toggleMute,
    toggleVideo,
  } = useCall();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);

  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);

  // Attach streams to video elements
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  // Always attach remote stream to audio element for audio playback
  useEffect(() => {
    if (!remoteAudioRef.current || !remoteStream) return;

    console.log('🔊 Attaching remote stream to audio element');

    // Debug: Log remote stream tracks
    const audioTracks = remoteStream.getAudioTracks();
    console.log('🔊 Remote audio tracks:', audioTracks.length);
    audioTracks.forEach(track => {
      console.log('🔊 Remote audio track:', track.label,
                  'enabled:', track.enabled,
                  'muted:', track.muted,
                  'readyState:', track.readyState);
    });

    remoteAudioRef.current.srcObject = remoteStream;

    // Track listener for cleanup to prevent memory leak
    let playOnInteraction = null;

    // CRITICAL: Must explicitly call play()
    remoteAudioRef.current.play()
      .then(() => console.log('✅ Remote audio playing'))
      .catch(err => {
        console.error('❌ Audio play failed:', err);
        // Try to play on user interaction
        playOnInteraction = () => {
          remoteAudioRef.current?.play()
            .then(() => {
              console.log('✅ Remote audio playing after interaction');
              document.removeEventListener('click', playOnInteraction);
              playOnInteraction = null;
            })
            .catch(e => console.error('Audio still failed:', e));
        };
        document.addEventListener('click', playOnInteraction);
      });

    // Cleanup: Always remove event listener on unmount or stream change
    return () => {
      if (playOnInteraction) {
        document.removeEventListener('click', playOnInteraction);
      }
    };
  }, [remoteStream]);

  // Call timer
  useEffect(() => {
    if (callStatus !== 'connected') return;

    const interval = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [callStatus]);

  // Auto-hide controls
  useEffect(() => {
    if (callStatus !== 'connected') return;

    const timeout = setTimeout(() => setShowControls(false), 5000);
    return () => clearTimeout(timeout);
  }, [showControls, callStatus]);

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle mute toggle
  const handleToggleMute = () => {
    const muted = toggleMute();
    setIsMuted(muted);
  };

  // Handle video toggle
  const handleToggleVideo = () => {
    const videoOff = toggleVideo();
    setIsVideoOff(videoOff);
  };

  // Only show when in a call
  if (!currentCall || callStatus === 'idle') {
    return null;
  }

  const isVideoCall = currentCall.type === 'video';
  const isConnected = callStatus === 'connected';
  const isCalling = callStatus === 'calling';
  const isConnecting = callStatus === 'connecting';

  // Get display info for the other party
  const otherPartyName = currentCall.calleeName || currentCall.callerName || 'Unknown';
  const otherPartyPhoto = currentCall.calleePhoto || currentCall.callerPhotoUrl;

  // Block browser back/forward during call
  useEffect(() => {
    if (callStatus === 'idle' || !currentCall) return;

    // Prevent back navigation
    const handlePopState = (e) => {
      e.preventDefault();
      window.history.pushState(null, '', window.location.href);
    };

    // Push a state so we can catch the back button
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    // Prevent page refresh/close during call
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'You are in an active call. Are you sure you want to leave?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [callStatus, currentCall]);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-gray-900"
      onMouseMove={() => setShowControls(true)}
      onClick={() => setShowControls(true)}
      style={{ pointerEvents: 'auto' }}
    >
      {/* Hidden audio element for remote audio - ALWAYS present */}
      {/* Use visibility: hidden instead of display: none for better audio handling */}
      <audio
        ref={remoteAudioRef}
        autoPlay
        playsInline
        style={{ position: 'absolute', visibility: 'hidden', width: 0, height: 0 }}
      />

      {/* Remote Video (Full Screen) */}
      {isVideoCall && remoteStream ? (
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="w-full h-full object-cover"
        />
      ) : (
        // Audio-only or waiting for video
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
          {otherPartyPhoto ? (
            <img
              src={otherPartyPhoto}
              alt={otherPartyName}
              className="w-32 h-32 rounded-full object-cover border-4 border-white/20"
            />
          ) : (
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <span className="text-5xl font-bold text-white">
                {otherPartyName?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Local Video (Picture-in-Picture) */}
      {isVideoCall && localStream && !isVideoOff && (
        <div className="absolute bottom-24 right-4 w-32 h-48 md:w-48 md:h-64 rounded-xl overflow-hidden shadow-2xl border-2 border-white/20">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover mirror"
          />
        </div>
      )}

      {/* Top Bar */}
      <div
        className={`absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/70 to-transparent transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-white font-semibold text-lg">{otherPartyName}</h2>
            <p className="text-white/70 text-sm">
              {isCalling && 'Calling...'}
              {isConnecting && 'Connecting...'}
              {isConnected && formatDuration(callDuration)}
            </p>
          </div>

          {/* Call quality indicator */}
          {isConnected && (
            <div className="flex items-center gap-1">
              <div className="w-1 h-3 bg-green-500 rounded-full" />
              <div className="w-1 h-4 bg-green-500 rounded-full" />
              <div className="w-1 h-5 bg-green-500 rounded-full" />
              <div className="w-1 h-6 bg-green-500 rounded-full" />
            </div>
          )}
        </div>
      </div>

      {/* Calling/Connecting Overlay */}
      {(isCalling || isConnecting) && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="text-center">
            {otherPartyPhoto ? (
              <img
                src={otherPartyPhoto}
                alt={otherPartyName}
                className="w-28 h-28 rounded-full object-cover border-4 border-white/30 mx-auto mb-4 animate-pulse"
              />
            ) : (
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mx-auto mb-4 animate-pulse">
                <span className="text-4xl font-bold text-white">
                  {otherPartyName?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
            )}
            <h2 className="text-white text-xl font-semibold mb-2">{otherPartyName}</h2>
            <p className="text-white/70">
              {isCalling ? 'Ringing...' : 'Connecting...'}
            </p>
          </div>
        </div>
      )}

      {/* Bottom Controls */}
      <div
        className={`absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="flex items-center justify-center gap-4">
          {/* Mute Button */}
          <button
            onClick={handleToggleMute}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
              isMuted ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-600'
            }`}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            )}
          </button>

          {/* Video Toggle (only for video calls) */}
          {isVideoCall && (
            <button
              onClick={handleToggleVideo}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                isVideoOff ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-600'
              }`}
              title={isVideoOff ? 'Turn on camera' : 'Turn off camera'}
            >
              {isVideoOff ? (
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          )}

          {/* End Call Button */}
          <button
            onClick={hangUp}
            className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center transition-all hover:scale-110"
            title="End call"
          >
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
            </svg>
          </button>
        </div>
      </div>

      {/* CSS for mirrored local video */}
      <style>{`
        .mirror {
          transform: scaleX(-1);
        }
      `}</style>
    </div>
  );
}
